import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'
import type { LessonTutorAvailabilityItem, LessonTutorFilters } from './interfaces'
import type { LessonTutorAvailabilityLabel } from './lesson-create'

import { useListsStore } from '@shared/model'

import { LessonTutorAvailability } from './enums'
import { useLessonEditStore } from './edit/store'
import { useDisciplineStore } from '../store'
import { useTutorsAvailabilityApi } from '../../api/lessons/availability.hook'
import { useLessonCheckScheduleApi } from '../../api/lessons/check-schedule.hook'
import { useLessonEditSave } from './lesson-edit-save.hook'
import {
  TUTOR_AVAILABILITY_LABELS,
  buildLessonTutorOptions,
  getDefaultTutorFilters,
  getLessonTypeCategories,
  getTutorAvailability,
} from './lesson-create'
import { getLessonTutorName } from './lessons'
import { getTutorReassignReasons } from './lesson-status'

interface LessonEditTutorModalReturn {
  refFilters: ComputedRef<LessonTutorFilters | null>
  refTutorId: WritableComputedRef<number | null>
  refTutorOptions: ComputedRef<AppSelectOption<number>[]>
  refIsTutorsLoading: Ref<boolean>
  refDate: ComputedRef<string>
  refSelectedLabel: ComputedRef<LessonTutorAvailabilityLabel | null>
  refIsSelectedBusy: ComputedRef<boolean>
  refKeptTutorName: ComputedRef<string | null>
  refIsOffSchedule: ComputedRef<boolean>
  refIsTutorReplaced: ComputedRef<boolean>
  refReassignOptions: ComputedRef<NativeSelectOption[]>
  refReassignReason: ComputedRef<string | null>
  refReassignComment: ComputedRef<string>
  refIsReassignInvalid: Ref<boolean>
  refSaveText: ComputedRef<string>
  refIsSaveDisabled: ComputedRef<boolean>
  refIsSaving: ComputedRef<boolean>
  setReassignReason: (option: NativeSelectOption) => void
  setReassignComment: (event: AppTextareaEvent) => void
  applyFilters: (filters: LessonTutorFilters) => Promise<void>
  clearFilters: () => Promise<void>
  save: () => Promise<void>
  close: () => void
}

/**
 * The tutor modal of the edit page, one screen, as the legacy tutor select behaved: the list is the tutors matching
 * the time and the filters applied above the select — nobody is added to it. It is read when the modal opens and
 * again on Apply filters and Clear; the applied filters are kept while the lesson is edited. The lesson's own tutor is shown
 * while listed, "-- Not selected --" otherwise, and stays on the lesson until the admin picks someone else. What the
 * pick entails shows up beside it: the schedule warning for a tutor off their schedule, the reason for a replaced
 * one; Save takes it all at once. Only a kept tutor the list does not show, at a new time, is asked about their
 * schedule separately, and Save then asks once more.
 */
export const useLessonEditTutorModal = (): LessonEditTutorModalReturn => {
  const route = useRoute()
  const store = useLessonEditStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const { save: saveLesson } = useLessonEditSave()

  const lesson = store.getLesson
  const disciplineId = route.params.id as string

  const refTutors = ref<LessonTutorAvailabilityItem[]>([])
  const refIsTutorsLoading = ref<boolean>(false)

  const refDraft = ref<number | null>(store.getTutorId)
  const refIsTouched = ref<boolean>(false)
  /** The separate schedule check refused the kept tutor: the warning is up and Save asks once more. */
  const refIsScheduleRefused = ref<boolean>(false)

  const refDate = computed<string>(() => store.getDate)

  const refIsSaving = computed<boolean>(() => store.isSaving)

  const refPaidLabels = computed<Record<string, string>>(() => listsStore.getLists?.tutor.paid_label ?? {})

  /** The filters applied so far, or null for the discipline's defaults. */
  const refFilters = computed<LessonTutorFilters | null>(() => store.getFilters)

  const refTutorOptions = computed<AppSelectOption<number>[]>(() => buildLessonTutorOptions(refTutors.value, refPaidLabels.value))

  const isListed = (tutorId: number | null): boolean => tutorId !== null && refTutors.value.some(tutor => tutor.id === tutorId)

  /** What the select shows: the pick while the list has it, else "-- Not selected --". */
  const refTutorId = computed<number | null>({
    get: () => (isListed(refDraft.value) ? refDraft.value : null),
    set: (value) => {
      refDraft.value = value
      refIsTouched.value = true
      refIsScheduleRefused.value = false
    },
  })

  /** The tutor the save carries: the pick once the admin touched the select, else the lesson's own tutor. */
  const refEffectiveTutorId = computed<number | null>(() => (refIsTouched.value ? refDraft.value : store.getTutorId))

  const refSelectedAvailability = computed<LessonTutorAvailability | null>(() =>
    getTutorAvailability(refTutors.value, refEffectiveTutorId.value))

  const refSelectedLabel = computed<LessonTutorAvailabilityLabel | null>(() =>
    (refSelectedAvailability.value ? TUTOR_AVAILABILITY_LABELS[refSelectedAvailability.value] : null))

  const refIsSelectedBusy = computed<boolean>(() => refSelectedAvailability.value === LessonTutorAvailability.Unavailable)

  const refKeptTutorName = computed<string | null>(() => {
    const tutor = lesson?.tutor
    if (!tutor || refIsTouched.value || refIsTutorsLoading.value || isListed(tutor.id)) return null

    return getLessonTutorName(tutor, refPaidLabels.value)
  })

  const refIsOffSchedule = computed<boolean>(() =>
    refSelectedAvailability.value === LessonTutorAvailability.OutOfSchedule || refIsScheduleRefused.value)

  /** Only replacing an assigned tutor needs a reason; giving a lesson its first tutor does not. */
  const refIsTutorReplaced = computed<boolean>(() =>
    lesson?.tutor_id !== null && lesson?.tutor_id !== undefined && refEffectiveTutorId.value !== lesson.tutor_id)

  const refReassignReason = computed<string | null>(() => store.getReassignReason)

  const refReassignOptions = computed<NativeSelectOption[]>(() => [
    { id: null, value: null, text: '--- Not selected ---', selected: !refReassignReason.value, disabled: true },
    ...Object.entries(getTutorReassignReasons(listsStore.getLists?.tutor.reassign_reason)).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refReassignReason.value,
    })),
  ])

  const refReassignComment = computed<string>(() => store.getReassignComment)

  const refIsReassignInvalid = ref<boolean>(false)

  const refSaveText = computed<string>(() => (refIsScheduleRefused.value ? 'Save anyway' : 'Save'))

  const refIsSaveDisabled = computed<boolean>(() => refIsTutorsLoading.value || refIsSaving.value || refIsSelectedBusy.value)

  const loadTutors = async (): Promise<void> => {
    const start = store.getStart
    if (!lesson || !start) return

    const filters = store.getFilters ?? getDefaultTutorFilters(disciplineStore.getDiscipline?.code)

    refIsTutorsLoading.value = true

    const tutors = await useTutorsAvailabilityApi({
      params: {
        start,
        discipline_id: Number(disciplineId),
        booking_id: lesson.id,
        gender: filters.gender,
        special_features: filters.special_features,
        accent: filters.accent,
        categories: filters.categories.length ? filters.categories : getLessonTypeCategories(lesson.type),
      },
    })

    refTutors.value = tutors ?? []
    refIsTutorsLoading.value = false

    // A pick the new list no longer has is dropped: the lesson's own tutor is back, untouched.
    if (refIsTouched.value && refDraft.value !== null && !isListed(refDraft.value)) {
      refDraft.value = store.getTutorId
      refIsTouched.value = false
    }
  }

  onMounted(loadTutors)

  const applyFilters = async (filters: LessonTutorFilters): Promise<void> => {
    store.setFilters(filters)
    await loadTutors()
  }

  const clearFilters = async (): Promise<void> => {
    store.setFilters(null)
    await loadTutors()
  }

  const setReassignReason = (option: NativeSelectOption): void => {
    store.setReassignReason(option.value ? String(option.value) : null)
    refIsReassignInvalid.value = false
  }

  const setReassignComment = (event: AppTextareaEvent): void => {
    store.setReassignComment(event.value)
  }

  /** A kept tutor the list does not show, at a new time: the list could not say whether they work then. */
  const isKeptTutorWithinSchedule = async (): Promise<boolean | null> => {
    const tutorId = refEffectiveTutorId.value
    if (tutorId === null || isListed(tutorId) || !lesson || store.getStart === lesson.start) return true

    store.setSaving(true)
    const isAllowed = await useLessonCheckScheduleApi({
      disciplineId,
      payload: { start: store.getStart, tutor_id: tutorId },
    })
    store.setSaving(false)

    return isAllowed
  }

  const save = async (): Promise<void> => {
    if (refIsSaveDisabled.value) return

    if (refIsTutorReplaced.value && !store.getReassignReason) {
      refIsReassignInvalid.value = true
      return
    }

    if (!refIsScheduleRefused.value) {
      const isWithinSchedule = await isKeptTutorWithinSchedule()
      if (isWithinSchedule === null) return

      if (!isWithinSchedule) {
        refIsScheduleRefused.value = true
        return
      }
    }

    if (refIsTouched.value) store.setTutorId(refDraft.value)

    const isSaved = await saveLesson()
    if (!isSaved) store.setTutorModalOpen(false)
  }

  const close = (): void => {
    if (refIsSaving.value) return

    store.setReassignReason(null)
    store.setReassignComment('')
    store.setTutorModalOpen(false)
  }

  return {
    refFilters,
    refTutorId,
    refTutorOptions,
    refIsTutorsLoading,
    refDate,
    refSelectedLabel,
    refIsSelectedBusy,
    refKeptTutorName,
    refIsOffSchedule,
    refIsTutorReplaced,
    refReassignOptions,
    refReassignReason,
    refReassignComment,
    refIsReassignInvalid,
    refSaveText,
    refIsSaveDisabled,
    refIsSaving,
    setReassignReason,
    setReassignComment,
    applyFilters,
    clearFilters,
    save,
    close,
  }
}
